import React, { useState, useEffect } from "react";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import useReveal from "@/hooks/useReveal";
import { PORTFOLIO_IMG } from "@/data/content";
import { FEATURED_ARTICLES } from "@/data/articles";
import { pickLocale } from "@/i18n";

/* ------------------------------------------------------------------ */
/* Assets                                                              */
/* ------------------------------------------------------------------ */
const LOGO_WHITE =
  "/assets/fekkib9r_LOGO-UNIK-HOLDINGS-White-gold.png";
const LOGO_COLOR =
  "/assets/jkqz26em_LOGO-UNIK-HOLDINGS-1.png";

const HERO_IMG =
  "/assets/aasppokb_Screen-Shot-2026-05-16.png";
const TEAM_IMG =
  "/assets/unsplash-1583417319070.jpg";
const SUSTAIN_IMG =
  "/assets/unsplash-1528181304800.jpg";

const FOCUS = [
  {
    id: "real-estate",
    title: "Real Estate",
    tagline: "Vision Beyond Structures",
    body: "Long-term ownership in residential, commercial, and industrial assets across Northern Vietnam — built for resilient income, sustainable communities, and intergenerational value.",
    link: "/investments",
    linkLabel: "View our real estate portfolio",
    img: "/assets/d75rcu64_real-estate.jpg",
  },
  {
    id: "healthcare",
    title: "Healthcare",
    tagline: "Making A Difference Everyday",
    body: "An integrated healthspan platform combining clinical care, destination wellness, longevity medicine, and applied research — pioneering preventive health across Asia.",
    link: "/investments",
    linkLabel: "Explore the Chitta ecosystem",
    img: "/assets/vyf42xpg_Chitta-Healthspan-2.png",
  },
  {
    id: "education",
    title: "Education",
    tagline: "By Generations. For Generations",
    body: "Developing the intellectual foundation through applied research, clinical training, and knowledge systems — building the human capital that underpins our long-term thesis.",
    link: "/impact",
    linkLabel: "Learn about our education initiatives",
    img: "/assets/n7sh1mqr_education-home.jpg",
  },
];

const PORTFOLIO = [
  {
    name: "Chitta Retreat",
    name_vi: "Chitta Retreat",
    sub: "Phu Tho — 81 ha",
    sub_vi: "Phú Thọ - 81 ha",
    slug: "chitta-hoa-binh",
    metric: "81",
    unit: "ha",
    unit_vi: "ha",
    img: PORTFOLIO_IMG.chittaRetreat,
  },
  {
    name: "Chitta Center Hanoi",
    name_vi: "Chitta Center Hà Nội",
    sub: "Ba Dinh — Urban Longevity Centre",
    sub_vi: "Ba Đình - Trung tâm Chăm sóc sức khỏe trường thọ",
    slug: "chitta-center-hanoi",
    metric: "400",
    unit: "m²",
    unit_vi: "m²",
    img: PORTFOLIO_IMG.chittaCenter,
  },
  {
    name: "Berriver Long Bien",
    name_vi: "Berriver Long Biên",
    sub: "Long Bien — Residential",
    sub_vi: "Long Biên - Nhà ở thương mại",
    slug: "berriver-long-bien",
    metric: "1,016",
    unit: "Units",
    unit_vi: "căn hộ",
    img: PORTFOLIO_IMG.berriver,
  },
  {
    name: "Thang Long Tower",
    name_vi: "Toà nhà Thăng Long",
    sub: "Thanh Xuan — Grade A Office",
    sub_vi: "Thanh Xuân - Văn phòng hạng A",
    slug: "thang-long-tower",
    metric: "13,156",
    unit: "m² NLA",
    unit_vi: "m² NLA",
    img: PORTFOLIO_IMG.thangLong,
  },
];

const NAV = [
  { label: "Home", href: "#top" },
  { label: "Portfolios", href: "#portfolio" },
  { label: "Services", href: "#focus" },
  { label: "Contact Us", href: "#contact" },
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function Landing() {
  useReveal();

  return (
    <div data-testid="landing-page" className="min-h-screen bg-white text-unik-ink">
      <SiteNav />
      <Hero />
      <About />
      <MountainDivider />
      <Focus />
      <Portfolio />
      <Sustainability />
      <News />
      <WorkWith />
      <SiteFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */
function Nav({ scrolled, mobileOpen, setMobileOpen }) {
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#14355a]/95 backdrop-blur-md border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between gap-8">
        <a href="#top" className="flex items-center flex-shrink-0" data-testid="nav-logo">
          <img
            src={LOGO_WHITE}
            alt="UNIK Holdings"
            className={`transition-all duration-500 ${scrolled ? "h-10" : "h-14"} w-auto`}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10 ml-auto">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-display text-[16px] font-medium tracking-wide text-white/90 hover:text-unik-gold transition-colors duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center" data-testid="nav-search">
          <div className="relative">
            <input
              type="text"
              placeholder="search"
              className="bg-transparent border border-white/40 text-white placeholder-white/60 rounded-full pl-5 pr-11 py-2.5 text-sm font-display focus:outline-none focus:border-unik-gold transition-colors w-56"
            />
            <Search
              size={15}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70"
            />
          </div>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-white"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[#0e2540] border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setMobileOpen(false)}
                className="font-display text-white/90 text-lg hover:text-unik-gold transition-colors"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              className="font-display inline-flex items-center gap-2 text-unik-gold mt-2"
            >
              Contact Us <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */
function Hero() {
  const { t } = useTranslation();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#14355a]"
    >
      {/* Video background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={HERO_IMG}
          src="/assets/ydbjg335_BG-Chitta-lq.mp4"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#14355a]/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2540]/40 via-transparent to-[#0e2540]/55" />
      </div>
      <div className="absolute inset-0 grain pointer-events-none opacity-[0.04]" />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-24 pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-5xl">
          <h1
            data-testid="hero-headline"
            className="font-display text-white leading-[0.95] tracking-tight"
          >
            <span
              className="fade-in block"
              style={{
                fontSize: "clamp(1.125rem, 2.25vw, 1.75rem)",
                fontWeight: 500,
                animationDelay: "0.1s",
              }}
            >
              {t("landing.heroEyebrow")}
            </span>
            <span
              className="fade-in block mt-4 lg:mt-5 tracking-tight"
              style={{
                fontSize: "clamp(2rem, 5.5vw, 5rem)",
                letterSpacing: "-0.025em",
                lineHeight: "1.05",
                fontWeight: 500,
                animationDelay: "0.45s",
              }}
            >
              <span className="block">
                {t("landing.heroTitleL1Before")}
                {t("landing.heroTitleL1Gold") ? <> <span className="text-unik-gold">{t("landing.heroTitleL1Gold")}</span></> : null}
                {t("landing.heroTitleL1After") ? <> {t("landing.heroTitleL1After")}</> : null}
              </span>
              <span className="block">
                {t("landing.heroTitleL2Before")}
                {t("landing.heroTitleL2Gold") ? <> <span className="text-unik-gold">{t("landing.heroTitleL2Gold")}</span></> : null}
                {t("landing.heroTitleL2After") ? <> {t("landing.heroTitleL2After")}</> : null}
              </span>
            </span>
          </h1>

          <p
            className="fade-in mt-8 lg:mt-10 max-w-2xl text-white/85 leading-[1.7]"
            style={{
              fontSize: "clamp(1rem, 1.25vw, 1.25rem)",
              animationDelay: "0.65s",
            }}
          >
            {t("landing.heroSub")}
          </p>

          <div
            className="fade-in mt-12 lg:mt-14 flex flex-wrap items-center gap-8 lg:gap-12"
            style={{ animationDelay: "0.85s" }}
          >
            <Link
              to="/about/what-we-do"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center px-12 py-4 rounded-full border border-unik-gold text-unik-gold font-display text-sm tracking-wide hover:bg-unik-gold hover:text-[#14355a] transition-all duration-500"
            >
              {t("landing.heroCtaPrimary")}
            </Link>
            <Link
              to="/investments/portfolio-assets"
              data-testid="hero-cta-secondary"
              className="link-rule text-white text-sm hover:text-unik-gold"
            >
              {t("landing.heroCtaSecondary")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* About                                                               */
/* ------------------------------------------------------------------ */
function About() {
  const { t } = useTranslation();
  const stats = [
    { val: 248, suffix: "+", unit: "M", label: t("landing.stats.capitalCommitted") },
    { val: 4, suffix: "", unit: "", label: t("landing.stats.operatingPlatforms") },
    { val: 86, suffix: "+", unit: "ha", label: t("landing.stats.developmentScale") },
    { val: 23, suffix: "+", unit: t("landing.stats.yearsSuffix"), label: t("landing.stats.trackRecord") },
  ];
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#f3f4f6] py-28 lg:py-40 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT: About Us label + big bold headline */}
          <div className="lg:col-span-7" data-reveal>
            <div
              className="eyebrow text-[#14355a]"
              style={{ fontSize: "clamp(14px, 1vw, 16px)", letterSpacing: "0.25em" }}
            >
              {t("landing.aboutEyebrow")}
            </div>
            <h2
              data-testid="about-headline"
              className="mt-10 font-display text-[#14355a] leading-[1.0] tracking-tight"
              style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)", fontWeight: 500 }}
            >
              {t("landing.aboutTitleLine1")}
              {t("landing.aboutTitleLine2Before") && <><br className="hidden lg:inline" />{" "}{t("landing.aboutTitleLine2Before")}</>}{" "}
              <span className="text-[#b48a3a]">{t("landing.aboutTitleLine2Gold")}</span>
              {t("landing.aboutTitleLine3") && <><br className="hidden lg:inline" />{" "}{t("landing.aboutTitleLine3")}</>}
              {t("landing.aboutTitleLine4Before") && <><br className="hidden lg:inline" />{" "}{t("landing.aboutTitleLine4Before")}</>}{" "}
              <span className="text-[#b48a3a]">{t("landing.aboutTitleLine4Gold")}</span>
            </h2>
          </div>

          {/* RIGHT: body copy + Learn More + stats */}
          <div className="lg:col-span-5 lg:pt-2">
            <div className="space-y-7 text-black">
              <p
                data-reveal
                style={{ "--reveal-delay": "120ms", fontSize: "clamp(17px, 1.3vw, 20px)", textAlign: "justify" }}
                className="leading-[1.85]"
              >
                {t("landing.aboutBody")}
              </p>
            </div>

            <Link
              to="/about"
              data-testid="about-learn-more"
              data-reveal
              style={{ "--reveal-delay": "320ms", fontSize: "15px" }}
              className="link-rule mt-12 text-[#14355a] hover:text-unik-gold-soft"
            >
              {t("landing.aboutCta")}
            </Link>

            {/* Stats — 2x2 grid with bigger numbers + animated count-up */}
            <div
              className="mt-16 lg:mt-20"
              data-testid="stats-block"
            >
              <div className="grid grid-cols-2 gap-x-4 gap-y-12 lg:gap-x-8">
                {stats.map((s, i) => (
                  <div
                    key={s.label}
                    data-testid={`stat-${i}`}
                    data-reveal
                    style={{ "--reveal-delay": `${i * 120}ms` }}
                    className={i % 2 === 1 ? "border-l border-[#14355a]/15 pl-6 lg:pl-8" : ""}
                  >
                    <div className="flex items-baseline gap-1">
                      <div
                        className="font-display text-[#14355a] tracking-tighter"
                        style={{ fontSize: "clamp(3rem, 4.5vw, 4.5rem)", fontWeight: 400, lineHeight: 1 }}
                      >
                        <CountUp end={s.val} duration={1800} delay={i * 120} />{s.suffix}
                      </div>
                      <div
                        className="font-display text-[#14355a]"
                        style={{ fontSize: "clamp(1rem, 1.2vw, 1.25rem)", fontWeight: 400 }}
                      >
                        {s.unit}
                      </div>
                    </div>
                    <div
                      className="mt-4 tracking-wider uppercase text-black/75 font-medium leading-[1.4]"
                      style={{ fontSize: "13px" }}
                    >
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* CountUp — IntersectionObserver-triggered number animation           */
/* ------------------------------------------------------------------ */
function CountUp({ end, duration = 1800, delay = 0 }) {
  const [value, setValue] = useState(0);
  const ref = React.useRef(null);
  const startedRef = React.useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now() + delay;
            const tick = (now) => {
              const t = Math.max(0, Math.min(1, (now - start) / duration));
              // easeOutCubic
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(end * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, delay]);

  return <span ref={ref}>{value.toLocaleString()}</span>;
}

/* ------------------------------------------------------------------ */
/* Mountain Divider — Vietnam wellness landscape                       */
/* ------------------------------------------------------------------ */
function MountainDivider() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="mountain-divider"
      className="relative w-full overflow-hidden bg-[#14355a]"
    >
      {/* Image */}
      <div className="relative w-full h-[58vh] lg:h-[72vh]">
        <img
          src="/assets/1avpfdxi_shake-hands.jpg"
          alt="Handshake — strategic partnership"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14355a]/30 via-transparent to-[#14355a]/40" />

        {/* Bottom strip — translucent gold band overlaid on image bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#b48a3a]/72 backdrop-blur-[1px] text-white">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-7 lg:py-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h3
              className="font-display tracking-tight uppercase"
              style={{
                fontSize: "clamp(2rem, 3.4vw, 3.5rem)",
                fontWeight: 500,
                lineHeight: 1.05,
                letterSpacing: "0.04em",
              }}
            >
              {t("landing.dividerTitle", "BUILT TO ENDURE.")}
            </h3>
            <Link
              to="/about"
              data-testid="built-to-endure-link"
              className="group inline-flex items-center gap-3 text-white font-display tracking-wide uppercase border-b border-white/60 hover:border-white pb-1 transition-colors duration-300"
              style={{ fontSize: "13px", letterSpacing: "0.18em", fontWeight: 500 }}
            >
              {t("landing.heroCtaPrimary")}
              <span className="inline-block w-8 h-px bg-white group-hover:w-12 transition-all duration-300" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Focus / Sectors                                                     */
/* ------------------------------------------------------------------ */
function Focus() {
  const { t } = useTranslation();
  return (
    <section
      id="focus"
      data-testid="focus-section"
      className="relative bg-unik-cream py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16 lg:mb-24">
          <div className="lg:col-span-7" data-reveal>
            <div className="eyebrow text-[#14355a]">{t("landing.investmentsEyebrow")}</div>
            <div className="mt-6 w-12 h-px bg-unik-gold" />
            <h2
              data-testid="focus-headline"
              className="mt-8 font-display text-[#14355a] leading-[1.0] tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 5.5vw, 5rem)" }}
            >
              {t("landing.investmentsTitle1")}<br />
              <span className="text-unik-navy-tint">{t("landing.investmentsTitle2")}</span>
            </h2>
          </div>
          <div className="lg:col-span-5" data-reveal style={{ "--reveal-delay": "120ms" }}>
            <p className="text-[17px] lg:text-[18px] leading-[1.85] text-black">
              {t("landing.investmentsBody")}
            </p>
          </div>
        </div>

        <FocusSlider />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Focus Slider — auto-rotating tab + slide                            */
/* ------------------------------------------------------------------ */
function FocusSlider() {
  const { t } = useTranslation();
  const focusItems = [
    {
      id: "real-estate",
      title: t("landing.sectors.realEstate.title"),
      tagline: t("landing.sectors.realEstate.eyebrow"),
      body: t("landing.sectors.realEstate.body"),
      link: "/investments",
      linkLabel: t("landing.sectors.realEstate.cta"),
      img: "/assets/d75rcu64_real-estate.jpg",
    },
    {
      id: "healthcare",
      title: t("landing.sectors.healthcare.title"),
      tagline: t("landing.sectors.healthcare.eyebrow"),
      body: t("landing.sectors.healthcare.body"),
      link: "/investments",
      linkLabel: t("landing.sectors.healthcare.cta"),
      img: "/assets/vyf42xpg_Chitta-Healthspan-2.png",
    },
    {
      id: "education",
      title: t("landing.sectors.education.title"),
      tagline: t("landing.sectors.education.eyebrow"),
      body: t("landing.sectors.education.body"),
      link: "/impact",
      linkLabel: t("landing.sectors.education.cta"),
      img: "/assets/n7sh1mqr_education-home.jpg",
    },
  ];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActive((prev) => (prev + 1) % focusItems.length);
    }, 5000);
    return () => clearInterval(t);
  }, [paused, focusItems.length]);

  const current = focusItems[active];

  return (
    <div
      data-testid="focus-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Tabs — centered */}
      <div className="flex flex-wrap items-center justify-center gap-2 lg:gap-3 mb-10 lg:mb-12 border-b border-[#14355a]/12">
        {focusItems.map((f, i) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setActive(i)}
            data-testid={`focus-tab-${f.id}`}
            className={`relative px-6 lg:px-8 py-4 lg:py-5 font-display tracking-tight transition-all duration-300 ${
              active === i
                ? "text-[#14355a]"
                : "text-[#14355a]/45 hover:text-[#14355a]/80"
            }`}
            style={{ fontSize: "clamp(1rem, 1.4vw, 1.25rem)", fontWeight: 500 }}
          >
            <span className="flex items-center gap-3">
              <span className="font-display text-[11px] tracking-[0.22em] opacity-60">
                0{i + 1}
              </span>
              {f.title}
            </span>
            {/* Active indicator */}
            <span
              className={`absolute left-0 right-0 -bottom-px h-[2px] bg-unik-gold transition-transform duration-500 origin-left ${
                active === i ? "scale-x-100" : "scale-x-0"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Slide content */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
        {/* Image */}
        <div className="lg:col-span-7 relative overflow-hidden bg-[#14355a]/5 aspect-[16/10] lg:aspect-auto lg:min-h-[460px]">
          {focusItems.map((f, i) => (
            <img
              key={f.id}
              src={f.img}
              alt={f.title}
              loading="lazy"
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1100ms] ease-out ${
                active === i
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-105"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540]/30 via-transparent to-transparent pointer-events-none" />
          {/* Auto-rotate progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/15">
            <div
              key={`${active}-${paused}`}
              className="h-full bg-unik-gold origin-left"
              style={{
                animation: paused ? "none" : "focusProgress 5s linear forwards",
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-5 flex flex-col justify-center">
          <div key={active} className="fade-in">
            <div className="eyebrow text-black">{current.tagline}</div>
            <h3
              className="mt-5 font-display text-[#14355a] tracking-tight"
              style={{
                fontSize: "clamp(2rem, 3.5vw, 3.25rem)",
                fontWeight: 500,
                lineHeight: 1.05,
              }}
            >
              {current.title}
            </h3>
            <p className="mt-7 text-[16px] lg:text-[17px] leading-[1.9] text-black max-w-lg">
              {current.body}
            </p>
            <Link
              to={current.link}
              data-testid={`focus-cta-${current.id}`}
              className="mt-10 inline-flex items-center gap-2 text-[#14355a] font-display text-xs font-medium tracking-[0.2em] uppercase border-b border-[#14355a]/40 hover:text-unik-gold hover:border-unik-gold pb-1 transition-all duration-300"
            >
              {current.linkLabel}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Portfolio                                                           */
/* ------------------------------------------------------------------ */
function Portfolio() {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    dragFree: false,
  });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
  }, [emblaApi]);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-white py-28 lg:py-40 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16 lg:mb-20"
          data-reveal
        >
          <div>
            <div className="eyebrow text-[#14355a]">{t("landing.portfolioEyebrow")}</div>
            <div className="mt-6 w-12 h-px bg-unik-gold" />
            <h2
              data-testid="portfolio-headline"
              className="mt-8 font-display text-[#14355a] leading-[1.0] tracking-tight max-w-3xl"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", fontWeight: 500 }}
            >
              {t("landing.portfolioTitle")}
            </h2>
          </div>

          <div className="flex items-center gap-6">
            <Link
              to="/investments/portfolio-assets"
              data-testid="portfolio-view-all"
              className="link-rule text-[#14355a] text-sm hover:text-unik-gold-soft"
            >
              {t("common.viewAll")}
            </Link>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Previous"
                data-testid="portfolio-prev"
                onClick={() => emblaApi && emblaApi.scrollPrev()}
                className="w-12 h-12 border border-[#14355a]/20 flex items-center justify-center text-[#14355a] hover:bg-[#14355a] hover:text-white transition-all duration-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                aria-label="Next"
                data-testid="portfolio-next"
                onClick={() => emblaApi && emblaApi.scrollNext()}
                className="w-12 h-12 border border-[#14355a]/20 flex items-center justify-center text-[#14355a] hover:bg-[#14355a] hover:text-white transition-all duration-300"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Embla carousel — seamless edge-to-edge */}
      <div className="relative" data-reveal-fade>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {PORTFOLIO.map((p, i) => (
              <div
                key={p.name}
                data-testid={`portfolio-slide-${i}`}
                className="flex-[0_0_88%] md:flex-[0_0_60%] lg:flex-[0_0_46%] xl:flex-[0_0_38%] pr-[2px]"
              >
                <Link to={`/asset/${p.slug}`} className="group relative overflow-hidden bg-[#14355a] block">
                  <div className="aspect-[5/4] overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540]/95 via-[#0e2540]/35 to-transparent" />
                  </div>
                  <div className="absolute inset-0 p-8 lg:p-12 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className="font-display text-unik-gold font-medium tracking-[0.2em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)", lineHeight: 1 }}>
                        0{i + 1}
                      </div>
                      <ArrowUpRight
                        size={22}
                        className="text-white/70 group-hover:text-unik-gold group-hover:rotate-12 transition-all duration-500"
                      />
                    </div>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <div className="font-display text-white font-medium text-4xl lg:text-5xl tracking-tighter">
                          {p.metric}
                        </div>
                        <div className="font-display text-unik-gold text-base lg:text-lg">
                          {pickLocale(p, "unit")}
                        </div>
                      </div>
                      <h3 className="mt-3 font-display text-white text-2xl lg:text-3xl font-medium">
                        {pickLocale(p, "name")}
                      </h3>
                      {p.sub && (
                        <p className="mt-2 text-white/85 text-base lg:text-[17px]">{pickLocale(p, "sub")}</p>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Progress / dots */}
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 mt-10 flex items-center gap-3">
          {PORTFOLIO.map((p, i) => (
            <button
              key={`dot-${p.slug}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`portfolio-dot-${i}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              className={`h-[2px] transition-all duration-500 ${
                selected === i ? "w-16 bg-[#14355a]" : "w-8 bg-[#14355a]/20"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Community Education Preview (replaces former Sustainability)       */
/* ------------------------------------------------------------------ */
function Sustainability() {
  const { t } = useTranslation();
  return (
    <section
      id="community-education"
      data-testid="community-education-section"
      className="relative bg-unik-cream overflow-hidden"
    >
      <div className="grid lg:grid-cols-2 min-h-[100vh]">
        {/* Image: full 50% width, full height */}
        <div
          className="relative h-[60vh] lg:h-auto overflow-hidden order-2 lg:order-1"
          data-reveal-fade
        >
          <img
            src="/assets/nw1bs1f2_knowledge.jpg"
            alt="Building knowledge that endures"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#14355a]/25 to-transparent" />
        </div>

        {/* Content */}
        <div className="flex items-center order-1 lg:order-2 py-24 lg:py-32 px-6 lg:px-16 xl:px-24">
          <div className="max-w-xl">
            <div data-reveal>
              <div className="eyebrow text-[#14355a]">{t("landing.impactEyebrow")}</div>
              <div className="mt-6 w-12 h-px bg-unik-gold" />
            </div>

            <h2
              data-testid="community-education-headline"
              data-reveal
              style={{ "--reveal-delay": "120ms" }}
              className="mt-8 font-display text-[#14355a] leading-[1.0] tracking-tight"
            >
              <span
                style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", fontWeight: 500 }}
                className="block text-unik-navy-tint"
              >
                {t("landing.impactTitle")}<br/>{t("landing.impactTitleLine2")}
              </span>
            </h2>

            <p
              data-reveal
              style={{ "--reveal-delay": "220ms" }}
              className="mt-8 text-[17px] lg:text-[18px] leading-[1.85] text-black"
            >
              {t("landing.impactBody")}
            </p>

            <div
              data-reveal
              style={{ "--reveal-delay": "320ms" }}
              className="mt-12 grid grid-cols-3 gap-px bg-[#14355a]/10"
              data-testid="ce-stats-grid"
            >
              {[
                { v: t("landing.impactPillars.research"), l: t("landing.impactPillars.researchSub") },
                { v: t("landing.impactPillars.knowledge"), l: t("landing.impactPillars.knowledgeSub") },
                { v: t("landing.impactPillars.human"), l: t("landing.impactPillars.capital") },
              ].map((s, i) => (
                <div
                  key={s.v}
                  className={`bg-[#f3f4f6] py-5 lg:py-6 pr-4 lg:pr-6 ${
                    i === 0 ? "pl-0" : "pl-5 lg:pl-7"
                  }`}
                >
                  <div className="font-display text-[#14355a] text-sm lg:text-base" style={{ fontWeight: 500 }}>
                    {s.v}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-unik-ink/55">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/impact"
              data-testid="community-education-cta"
              data-reveal
              style={{ "--reveal-delay": "420ms" }}
              className="link-rule mt-12 text-[#14355a] text-sm hover:text-unik-gold-soft"
            >
              {t("common.learnMore")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Team                                                                */
/* ------------------------------------------------------------------ */
function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-[#14355a] text-white"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-24 lg:pt-32 pb-12 lg:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6" data-reveal>
            <div className="eyebrow text-unik-gold">Team</div>
            <div className="mt-6 w-12 h-px bg-unik-gold" />
            <h2
              data-testid="team-headline"
              className="mt-8 font-display leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(2rem, 4.2vw, 3.75rem)" }}
            >
              Long-term alignment and global expertise.
            </h2>
          </div>
          <div
            className="lg:col-span-6 lg:pt-20"
            data-reveal
            style={{ "--reveal-delay": "150ms" }}
          >
            <p className="text-white/80 text-[15px] lg:text-base leading-[1.85]">
              With diverse nationalities, deep sector fluency, and decades of
              operating experience, our team carries a shared ownership mindset
              and a commitment to building lasting value for all stakeholders.
            </p>
            <a
              href="#"
              data-testid="team-cta"
              className="link-rule mt-8 text-unik-gold text-sm"
            >
              Meet the Team
            </a>
          </div>
        </div>
      </div>

      {/* Wide image breakout */}
      <div
        className="relative h-[42vh] lg:h-[58vh] overflow-hidden"
        data-reveal-fade
      >
        <img
          src={TEAM_IMG}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540]/40 via-transparent to-[#14355a]/30" />
        <div className="absolute bottom-6 lg:bottom-10 right-6 lg:right-10">
          <a
            href="#"
            data-testid="team-philosophy"
            className="link-rule text-white text-sm hover:text-unik-gold"
          >
            Investment Philosophy
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* News                                                                */
/* ------------------------------------------------------------------ */
function News() {
  const { t } = useTranslation();
  // Show the 3 featured Chitta articles
  const items = FEATURED_ARTICLES;
  return (
    <section
      id="news"
      data-testid="news-section"
      className="relative bg-white py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4" data-reveal>
            <div className="eyebrow text-[#14355a]">{t("landing.insightsEyebrow")}</div>
            <div className="mt-6 w-12 h-px bg-unik-gold" />
            <h2
              data-testid="news-headline"
              className="mt-8 font-display text-[#14355a] leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
            >
              {t("landing.insightsTitle")}
            </h2>
            <Link
              to="/insights"
              data-testid="news-view-all"
              className="link-rule mt-8 text-[#14355a] text-sm hover:text-unik-gold-soft"
            >
              {t("common.viewAll")}
            </Link>
          </div>

          <div className="lg:col-span-8 divide-y divide-[#14355a]/15">
            {items.map((n, i) => (
              <Link
                key={n.slug}
                to={`/insights/${n.slug}`}
                data-testid={`news-item-${i}`}
                data-reveal
                style={{ "--reveal-delay": `${i * 120}ms` }}
                className="group flex flex-col lg:flex-row gap-4 lg:gap-10 py-8 lg:py-10 first:pt-0 hover:pl-3 transition-all duration-500"
              >
                <div className="lg:w-32 flex-shrink-0">
                  <div className="eyebrow text-[#14355a]">{n.date}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-[#14355a] text-xl lg:text-2xl leading-tight group-hover:text-unik-navy-tint transition-colors">
                    {pickLocale(n, "title")}
                  </h3>
                  <p className="mt-3 text-[16px] leading-[1.85] text-black max-w-2xl">
                    {pickLocale(n, "excerpt")}
                  </p>
                </div>
                <ArrowRight
                  size={22}
                  className="hidden lg:block flex-shrink-0 text-[#14355a]/40 group-hover:text-unik-gold group-hover:translate-x-1 transition-all duration-500 self-center"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Work With — simple, compact strip                                   */
/* ------------------------------------------------------------------ */
function WorkWith() {
  const { t } = useTranslation();
  return (
    <section
      id="contact"
      data-testid="work-section"
      className="relative text-white overflow-hidden"
    >
      <div className="absolute inset-0 silk-bg" />
      <div className="absolute inset-0 grain pointer-events-none opacity-[0.04]" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7" data-reveal>
            <div className="eyebrow text-unik-gold">{t("landing.ctaEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                fontWeight: 500,
                lineHeight: "1.05",
              }}
              className="mt-5 font-display tracking-tight"
            >
              {t("landing.ctaTitleBefore")}{" "}
              <span className="text-unik-gold">{t("landing.ctaTitleGold")}</span>
              {t("landing.ctaTitleAfter") ? <> {t("landing.ctaTitleAfter")}</> : null}
            </h2>
            <p className="mt-5 max-w-xl text-white/85 text-[16px] lg:text-[17px] leading-[1.85]">
              {t("landing.ctaBody")}
            </p>
          </div>

          <div
            className="lg:col-span-5 flex flex-wrap items-center gap-6 lg:gap-8 lg:justify-end"
            data-reveal
            style={{ "--reveal-delay": "180ms" }}
          >
            <Link
              to="/contact#invest"
              data-testid="work-cta-investor"
              className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-unik-gold text-[#14355a] font-display text-sm tracking-wide hover:bg-unik-gold-soft transition-all duration-500"
            >
              {t("landing.ctaInvestor")}
              <ArrowUpRight size={16} className="group-hover:rotate-12 transition-transform" />
            </Link>
            <Link
              to="/contact#partnerships"
              data-testid="work-cta-partner"
              className="link-rule text-white text-sm hover:text-unik-gold"
            >
              {t("landing.ctaPartner")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative text-white overflow-hidden"
    >
      <div className="absolute inset-0 silk-bg" />
      <div className="absolute inset-0 grain pointer-events-none opacity-[0.05]" />
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-white/15">
          <div className="lg:col-span-5">
            <img src={LOGO_WHITE} alt="UNIK Holdings" className="h-16 w-auto" />
            <p className="mt-8 max-w-md text-white/70 text-[15px] leading-[1.85]">
              Think Long. Build Well. Stay Human. — A long-term investment group
              committed to disciplined capital allocation, active stewardship,
              and building enduring value for generations to come.
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow text-unik-gold">Quick Links</div>
            <ul className="mt-6 space-y-3">
              {["Home", "About Us", "Investment", "Sustainability", "Insights & News", "Contact Us"].map((l) => (
                <li key={l}>
                  <a
                    href="#"
                    className="text-white/80 hover:text-unik-gold text-[14px] transition-colors"
                    data-testid={`footer-link-${l.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="eyebrow text-unik-gold">Privacy</div>
            <ul className="mt-6 space-y-3">
              {["Privacy Policy", "Terms & Conditions", "FAQ"].map((l) => (
                <li key={l}>
                  <a
                    href="#"
                    className="text-white/80 hover:text-unik-gold text-[14px] transition-colors"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>

            <div className="eyebrow text-unik-gold mt-10">Headquarters</div>
            <p className="mt-4 text-white/80 text-[14px] leading-[1.8]">
              UNIK Holdings<br />
              Vietnam
            </p>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-white/55 text-xs tracking-wider">
            © 2026 UNIK HOLDINGS. ALL RIGHTS RESERVED.
          </p>
          <div className="flex items-center gap-8 text-xs tracking-wider text-white/55">
            <span>VIETNAM</span>
            <span className="w-px h-3 bg-white/20" />
            <span>EST. — LONG-TERM</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
